import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { staffMe } from "@/utils/staffAuth";

/**
 * Sep 23 2026 · Mushka (Medical Director) walled-off surface. Only role
 * `medical_director` OR `owner` can reach /md/*. Any other role gets
 * bounced to /staff (their own landing) so they can't wander into
 * Mushka's chart view.
 *
 * Note: this gate INTENTIONALLY does NOT hydrate the admin passcode
 * (Mushka must never touch admin surfaces) and does NOT mount the
 * LiveCheckinBanner (that's for front-desk staff, not the MD).
 */
export default function MDAuthGate({ children }) {
  const location = useLocation();
  const [state, setState] = useState({ status: "checking", user: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await staffMe();
      if (cancelled) return;
      if (!u) {
        setState({ status: "denied", user: null });
        return;
      }
      if (u.role !== "medical_director" && u.role !== "owner") {
        setState({ status: "denied-role", user: u });
        return;
      }
      setState({ status: "ok", user: u });
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (state.status === "checking") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400"
        data-testid="md-auth-gate-loading"
      >
        <div className="text-sm">Verifying session…</div>
      </div>
    );
  }

  if (state.status === "denied") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/staff/login?next=${next}`} replace />;
  }

  if (state.status === "denied-role") {
    return <Navigate to="/staff" replace />;
  }

  return children;
}
